import dayjs from "dayjs";

import { colorsPalettes } from "@similarweb/styles";

import type ColorStack from "app/components/colorsStack/ColorStack";
import { serverResponseFormat } from "@similarweb/pro-logic-components";
import { translate } from "app/filters/i18nFilter";
import type { GraphData } from "app/common/keyword-research/clicks-over-time-graph/graphDataUtils";

import type { IShareOfVoiceGraphData, ITransformedDataPoint, TotalsData } from "./types";

export const OTHERS = "Others";
const DEFAULT_SELECTED_DOMAINS_COUNT = 5;

const checkIfAllZero = (data: GraphData): boolean =>
    Object.values(data).every((site) => Object.values(site).every((value) => value === 0));

export const getOthersMonthlyAvg = (totalsData: TotalsData, graphDataItems: ITransformedDataPoint[]) => {
    const totalSum = Object.values(totalsData)?.reduce((sum, value) => sum + value, 0);
    const monthsCount = Object.keys(totalsData)?.length;
    const sitesTotalMonthlyAvgClicks = graphDataItems
        ?.filter((item) => item.visible)
        .reduce((sum, site) => sum + site.total, 0);
    return totalSum / monthsCount - sitesTotalMonthlyAvgClicks;
};

export const getOthersData = (totalsData: TotalsData, graphDataItems: ITransformedDataPoint[]) => {
    return Object.entries(totalsData).map(([date, totalValue]) => {
        const timestampValue = dayjs.utc(date, serverResponseFormat).valueOf();
        const visibleGraphDataItemsDataPointSum = graphDataItems
            ?.filter((item) => item.visible)
            .reduce((sum, site) => {
                const siteDataPoint = site.data.find((point) => point[0] === timestampValue)?.[1];
                return sum + (siteDataPoint || 0);
            }, 0);

        const othersValue = totalValue - visibleGraphDataItemsDataPointSum;
        return [timestampValue, othersValue];
    });
};

export const transformData = (
    response: IShareOfVoiceGraphData,
    domains: string[],
    colorStack: ColorStack,
): {
    graphDataItems: ITransformedDataPoint[];
    totalsData: TotalsData;
    sitesClicks: GraphData;
} => {
    const totalsData = response.Totals || {};
    const sitesClicks = response.SitesClicks;
    const siteClicksData = response.SitesClicksData || {};

    if (checkIfAllZero(sitesClicks)) {
        return { graphDataItems: null, totalsData, sitesClicks };
    }

    colorStack.reset();

    const sitesItems: ITransformedDataPoint[] = Object.entries(sitesClicks).reduce((items, [site, siteData]) => {
        if (!domains.includes(site)) {
            return items;
        }

        const data = Object.entries(siteData).map(([date, value]) => {
            const timestampValue = dayjs.utc(date, serverResponseFormat).valueOf();
            return [timestampValue, value];
        });

        const monthlyAvg = siteClicksData[site] ? (Object.values(siteClicksData[site])?.[0]?.DomainAvgClicks ?? 0) : 0;

        const item = {
            name: site,
            id: site,
            data,
            visible: false,
            color: colorsPalettes.carbon[200],
            total: monthlyAvg,
        };
        return [item, ...items];
    }, [] as ITransformedDataPoint[]);

    sitesItems.sort((a, b) => b.total - a.total);
    const itemsWithVisibilitySet = sitesItems.map((item, index) => ({
        ...item,
        visible: index < DEFAULT_SELECTED_DOMAINS_COUNT,
        color: index < DEFAULT_SELECTED_DOMAINS_COUNT ? (colorStack.acquire() as string) : item.color,
    }));

    // Calculate others data:
    // ((total clicks for current date - sum of all domains clicks for current date) / total clicks for current date)
    const othersData = getOthersData(totalsData, itemsWithVisibilitySet);

    // Calculate others monthly average:
    // (avg of all total clicks - sum of all avg of domains clicks)
    const othersMonthlyAvg = getOthersMonthlyAvg(totalsData, itemsWithVisibilitySet);

    itemsWithVisibilitySet.push({
        name: OTHERS,
        id: OTHERS,
        data: othersData,
        visible: true,
        color: colorsPalettes.carbon[200],
        total: othersMonthlyAvg,
        isOther: true,
    });

    return { graphDataItems: itemsWithVisibilitySet, totalsData, sitesClicks };
};

export const getNoDataObject = (isError: boolean) =>
    isError
        ? {
              title: translate("keyword.research.search.ads.table.error.title"),
              subtitle: translate("keyword.research.search.ads.table.error.subtitle"),
          }
        : {
              title: translate("keyword.research.search.ads.table.no.data.title"),
              subtitle: translate("keyword.research.search.ads.table.no.data.subtitle"),
          };
